/**
 * Boot Pullback — renderer canvas-only.
 *
 * Una única custom series (BootPullbackSeries) pinta todo lo que se mueve con
 * el chart, en passes ordenados: sombreado de flujo, niveles congelados,
 * niveles activos Ext/Int, zigzags, remnants de anclas, marcadores de swing
 * y etiquetas CHoCH/BOS con anti-colisión en píxeles de pantalla.
 *
 * Decisión de color documentada: Ext H se pinta con colorBull y Ext L con
 * colorBear (su ruptura genera ese flujo). Las etiquetas se colorean por
 * dirección de ruptura (up = bull, down = bear), de modo que el texto de una
 * ruptura siempre coincide con el color de la línea del nivel que rompió.
 */

import { customSeriesDefaultOptions } from 'lightweight-charts'
import { resolveConfig, LABEL_FONT } from './constants.js'

function withAlpha(color, alpha) {
  if (typeof color !== 'string') return `rgba(55,65,81,${alpha})`
  if (color.startsWith('rgba')) {
    return color.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/, `rgba($1, $2, $3, ${alpha})`)
  }
  if (color.startsWith('rgb(')) {
    const parts = color.match(/\d+/g)
    return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`
  }
  if (color.startsWith('#')) {
    const hex = color.replace('#', '')
    if (hex.length === 6) {
      const n = parseInt(hex, 16)
      return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
    }
  }
  return color
}

function legColorOf(color, cfg) {
  if (color === 'long') return cfg.colorBull
  if (color === 'short') return cfg.colorBear
  if (color === 'transition') return cfg.colorAccent
  return null
}

class BootPullbackPaneRenderer {
  constructor() {
    this._data = null
    this._options = null
  }

  update(data, options) {
    this._data = data
    this._options = options
  }

  draw(target, priceConverter) {
    if (!this._data || !this._options) return
    const { bars, visibleRange } = this._data
    if (!bars || bars.length === 0) return

    let payload = null
    for (let i = 0; i < bars.length; i++) {
      const p = bars[i]?.originalData?.payload
      if (p) {
        payload = p
        break
      }
    }
    if (!payload || !payload.result || payload.result.error) return
    const { result, cfg } = payload

    // Lookup time(seconds) -> x en píxeles. OJO: lightweight-charts consume el
    // campo `time` del dato y NO lo conserva en originalData; por eso cada fila
    // lleva el timestamp duplicado en `t`. Las barras fuera de la ventana
    // visible llegan con x no finito; para esos tiempos se clampa al borde.
    const xByTime = new Map()
    let minVisibleTime = Infinity
    let maxVisibleTime = -Infinity
    for (let i = 0; i < bars.length; i++) {
      const b = bars[i]
      const t = b?.originalData?.t
      if (typeof t !== 'number') continue
      if (Number.isFinite(b.x)) {
        xByTime.set(t, b.x)
        if (t < minVisibleTime) minVisibleTime = t
        if (t > maxVisibleTime) maxVisibleTime = t
      }
    }

    target.useMediaCoordinateSpace(scope => {
      const ctx = scope.context
      const width = scope.mediaSize?.width ?? 0
      const height = scope.mediaSize?.height ?? 0
      if (width <= 0 || height <= 0) return

      const barSpacing = Number.isFinite(this._data.barSpacing) && this._data.barSpacing > 0
        ? this._data.barSpacing
        : 6
      const halfBar = barSpacing / 2

      const xAt = (t) => {
        if (typeof t !== 'number') return null
        const hit = xByTime.get(t)
        if (Number.isFinite(hit)) return hit
        if (t < minVisibleTime) return 0
        if (t > maxVisibleTime) return width
        return null
      }
      const yAt = (price) => {
        if (!Number.isFinite(price)) return null
        const y = priceConverter(price)
        return y == null || Number.isNaN(y) ? null : y
      }

      const external = result.external
      const internal = result.internal
      const internalOn = cfg.showInternalStructure && internal != null

      // ── Pass 1: sombreado de flujo ──────────────────────────────────────
      if (cfg.showShading) {
        const drawLegs = (legs, alpha) => {
          for (const leg of legs || []) {
            const color = legColorOf(leg.color, cfg)
            if (!color) continue
            const x1 = xAt(leg.fromTime)
            const x2 = xAt(leg.toTime)
            const yTop = yAt(leg.top)
            const yBottom = yAt(leg.bottom)
            if (x1 == null || x2 == null || yTop == null || yBottom == null) continue
            const left = Math.min(x1, x2) + halfBar
            const right = Math.max(x1, x2) - halfBar
            if (right <= left) continue
            ctx.fillStyle = withAlpha(color, alpha)
            ctx.fillRect(left, yTop, right - left, yBottom - yTop)
          }
        }
        drawLegs(external?.flowLegs, 0.14)
        if (internalOn) drawLegs(internal.flowLegs, 0.08)
      }

      // ── Pass 2: niveles congelados (deduplicados por nivel+tiempo) ──────
      const seenFrozen = new Set()
      const drawFrozen = (levels) => {
        for (const lv of levels || []) {
          const key = `${lv.side}|${lv.price}|${lv.fromTime}`
          if (seenFrozen.has(key)) continue
          seenFrozen.add(key)
          const x1 = xAt(lv.fromTime)
          const x2 = xAt(lv.toTime)
          const y = yAt(lv.price)
          if (x1 == null || x2 == null || y == null) continue
          ctx.save()
          ctx.globalAlpha = 0.45
          ctx.strokeStyle = lv.side === 'H' ? cfg.colorBull : cfg.colorBear
          ctx.lineWidth = 1
          ctx.setLineDash([4, 4])
          ctx.beginPath()
          ctx.moveTo(x1, y)
          ctx.lineTo(x2, y)
          ctx.stroke()
          ctx.restore()
        }
      }
      if (cfg.showExtLevels) {
        drawFrozen(external?.levels)
        if (internalOn && cfg.showIntLevels) drawFrozen(internal.levels)
      }

      // ── Pass 3: niveles activos Ext/Int + etiquetas de borde derecho ────
      ctx.font = LABEL_FONT
      ctx.textBaseline = 'middle'
      const drawActiveLevel = (level, color, text, dashed, alpha) => {
        if (!level) return
        const y = yAt(level.price)
        if (y == null || y < -20 || y > height + 20) return
        const x1 = xAt(level.time)
        if (x1 == null) return
        ctx.save()
        if (alpha != null) ctx.globalAlpha = alpha
        ctx.strokeStyle = color
        ctx.lineWidth = 1
        if (dashed) ctx.setLineDash([4, 4])
        ctx.beginPath()
        ctx.moveTo(x1, y)
        ctx.lineTo(width, y)
        ctx.stroke()
        ctx.setLineDash([])
        ctx.fillStyle = color
        ctx.textAlign = 'right'
        ctx.fillText(text, width - 4, y - 5)
        ctx.restore()
      }
      if (cfg.showExtLevels && external) {
        drawActiveLevel(external.extH, cfg.colorBull, 'Ext H', false)
        drawActiveLevel(external.extL, cfg.colorBear, 'Ext L', false)
        if (cfg.showExtMid && external.extH && external.extL) {
          const mid = (external.extH.price + external.extL.price) / 2
          const fromTime = Math.min(external.extH.time, external.extL.time)
          drawActiveLevel({ price: mid, time: fromTime }, cfg.colorNeutral, 'Ext Mid', true)
        }
        if (internalOn && cfg.showIntLevels) {
          drawActiveLevel(internal.extH, cfg.colorBull, 'Int H', false, 0.55)
          drawActiveLevel(internal.extL, cfg.colorBear, 'Int L', false, 0.55)
        }
      }

      // ── Pass 4: zigzags ──────────────────────────────────────────────────
      const drawZigzag = (points, lineWidth, color) => {
        if (!points || points.length < 2) return
        ctx.save()
        ctx.strokeStyle = color
        ctx.lineWidth = lineWidth
        ctx.beginPath()
        let started = false
        for (const p of points) {
          const x = xAt(p.time)
          const y = yAt(p.price)
          if (x == null || y == null) continue
          if (!started) {
            ctx.moveTo(x, y)
            started = true
          } else ctx.lineTo(x, y)
        }
        if (started) ctx.stroke()
        ctx.restore()
      }
      if (cfg.showExternalStructure) drawZigzag(external?.points, 1.5, cfg.colorNeutral)
      if (internalOn && cfg.showInternalZigzag) drawZigzag(internal.points, 1, withAlpha(cfg.colorNeutral, 0.6))

      // ── Pass 5: remnants de anclas refinadas ─────────────────────────────
      const drawRemnants = (points) => {
        for (const p of points || []) {
          if (!p.remnant) continue
          const x1 = xAt(p.remnant.time)
          const x2 = xAt(p.time)
          const y = yAt(p.remnant.price)
          if (x1 == null || x2 == null || y == null) continue
          ctx.save()
          ctx.strokeStyle = cfg.colorNeutral
          ctx.lineWidth = 1
          ctx.setLineDash([2, 2])
          ctx.beginPath()
          ctx.moveTo(x1, y)
          ctx.lineTo(x2, y)
          ctx.stroke()
          ctx.restore()
        }
      }
      drawRemnants(external?.points)
      if (internalOn) drawRemnants(internal.points)

      // ── Pass 6: marcadores de swing ──────────────────────────────────────
      if (cfg.showSwingMarkers) {
        const drawMarkers = (points) => {
          for (const p of points || []) {
            // Lookup estricto: un punto fuera de la ventana visible se omite,
            // nunca se aplasta contra el borde del panel.
            const x = xByTime.get(p.time)
            if (!Number.isFinite(x)) continue
            const y = yAt(p.price)
            if (y == null) continue
            const isAnchorDebug = p.isAnchor && cfg.debugAnchors
            ctx.beginPath()
            ctx.arc(x, y, isAnchorDebug ? 3 : 2, 0, Math.PI * 2)
            ctx.fillStyle = isAnchorDebug ? cfg.colorAccent : cfg.colorNeutral
            ctx.fill()
          }
        }
        drawMarkers(external?.points)
        if (internalOn) drawMarkers(internal.points)
      }

      // ── Pass 7: etiquetas CHoCH/BOS con anti-colisión compartida ────────
      if (cfg.showLabels) {
        const seenLabels = new Set()
        const labels = []
        const collect = (breaks) => {
          for (const b of breaks || []) {
            if (!b.label) continue
            const key = `${b.label}|${b.labelToTime}|${b.labelPrice}`
            if (seenLabels.has(key)) continue
            seenLabels.add(key)
            labels.push(b)
          }
        }
        collect(external?.breaks)
        if (internalOn) collect(internal.breaks)
        labels.sort((a, b) => a.labelToTime - b.labelToTime)

        const fontSize = 10
        const lineHeight = fontSize + 4
        const margin = 2
        const placed = []
        ctx.font = LABEL_FONT
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        for (const b of labels) {
          const x1 = xAt(b.labelFromTime)
          const x2 = xAt(b.labelToTime)
          const y = yAt(b.labelPrice)
          if (x1 == null || x2 == null || y == null) continue
          const dx = (x1 + x2) / 2 + (cfg.labelOffsetX ?? 0)
          if (dx < -50 || dx > width + 50) continue
          const baseY = y - 4 // siempre encima de la línea del nivel roto
          const text = String(b.label)
          const textWidth = ctx.measureText(text).width
          const rectX = dx - textWidth / 2

          let finalY = baseY
          let fits = false
          for (let attempt = 0; attempt < 5; attempt++) {
            const rect = {
              x: rectX - margin,
              y: finalY - lineHeight / 2 - margin,
              w: textWidth + margin * 2,
              h: lineHeight + margin * 2,
            }
            const overlap = placed.some(p =>
              rect.x < p.x + p.w && rect.x + rect.w > p.x &&
              rect.y < p.y + p.h && rect.y + rect.h > p.y)
            if (!overlap) {
              fits = true
              break
            }
            finalY -= lineHeight
          }
          if (!fits) continue
          if (finalY < -20 || finalY > height + 20) continue

          ctx.fillStyle = b.dir === 'up' ? cfg.colorBull : cfg.colorBear
          ctx.fillText(text, dx, finalY)
          placed.push({
            x: rectX - margin,
            y: finalY - lineHeight / 2 - margin,
            w: textWidth + margin * 2,
            h: lineHeight + margin * 2,
          })
        }
      }
    })
  }
}

export class BootPullbackSeries {
  constructor() {
    this._renderer = new BootPullbackPaneRenderer()
  }

  renderer() {
    return this._renderer
  }

  update(data, seriesOptions) {
    this._renderer.update(data, seriesOptions)
  }

  priceValueBuilder(plotRow) {
    return Number.isFinite(plotRow?.value) ? [plotRow.value] : []
  }

  isWhitespace(data) {
    return data == null || data.payload == null
  }

  defaultOptions() {
    return {
      ...customSeriesDefaultOptions,
      color: 'rgba(0,0,0,0)',
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
    }
  }
}

/**
 * render() del indicador: crea la custom series a través del api para que el
 * cleanup por indicador funcione, y alimenta una fila por vela con el payload
 * (result + cfg) por referencia. Nunca muta candleSeries.
 */
export function renderBootPullback(chart, candleSeries, result, config, api) {
  const cfg = resolveConfig(config)
  if (!result || result.error) return
  if (!api || typeof api.addCustomSeries !== 'function') return
  const candles = result._candles
  if (!Array.isArray(candles) || candles.length === 0) return

  const series = api.addCustomSeries(new BootPullbackSeries(), {
    color: 'rgba(0,0,0,0)',
    lastValueVisible: false,
    priceLineVisible: false,
    crosshairMarkerVisible: false,
    autoscaleInfoProvider: () => null,
  })
  if (!series) return

  const payload = { result, cfg }
  // `time` lo consume LWC para la escala horizontal (no sobrevive en
  // originalData); `t` duplica el timestamp para el lookup time->x del draw.
  series.setData(candles.map(c => ({ time: c.time, value: c.close, t: c.time, payload })))
}
